import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { clearUploadProgress } from '@/store/slices/poc/heliaSlice';
import { finishCloning } from '@/store/slices/poc/cloneQueueSlice';
import { pollForClonedFiles } from '@/store/thunks/sixDetail/pollForClonedFiles';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import {
  isIncomingMessageOfType,
  type IncomingMessageOfType,
} from '@/store/listeners/poc/incomingMessageMatchers';
import {
  handleReplicationFailed,
  handleReplicationProgress,
  handleReplicationStarted,
  stopAllReplicationTracking,
  stopReplicationTrackingByCid,
  stopReplicationTrackingByUploadId,
} from '@/store/listeners/poc/uploadReplicationCoordinator';
import type { AppStartListening } from '@/store/listeners/types';

export function registerIncomingReplicationListeners(
  startAppListening: AppStartListening
): void {
  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-replication-started'>> =>
      isIncomingMessageOfType(action, 'file-replication-started'),
    effect: (action, listenerApi) => {
      handleReplicationStarted(listenerApi, {
        cid: action.payload.result.data.cid,
        totalBytes: action.payload.result.data.totalBytes,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-replication-progress'>> =>
      isIncomingMessageOfType(action, 'file-replication-progress'),
    effect: (action, listenerApi) => {
      handleReplicationProgress(listenerApi, {
        cid: action.payload.result.data.cid,
        stage: action.payload.result.data.stage,
        bytesTransferred: action.payload.result.data.bytesTransferred,
        totalBytes: action.payload.result.data.totalBytes,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-replication-failed'>> =>
      isIncomingMessageOfType(action, 'file-replication-failed'),
    effect: (action, listenerApi) => {
      handleReplicationFailed(listenerApi, {
        cid: action.payload.result.data.cid,
        reason: action.payload.result.data.reason,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-cloned'>> =>
      isIncomingMessageOfType(action, 'file-cloned'),
    effect: (action, listenerApi) => {
      const { sixId } = action.payload;
      const { cid } = action.payload.result.data;

      stopReplicationTrackingByCid(listenerApi, cid);

      const currentClone = listenerApi.getState().cloneQueue.currentlyCloning;
      if (currentClone?.cid === cid) {
        listenerApi.dispatch(finishCloning());
      }

      void listenerApi.dispatch(pollForClonedFiles({ sixId, cid }));
    },
  });

  startAppListening({
    actionCreator: clearUploadProgress,
    effect: (action) => {
      stopReplicationTrackingByUploadId(action.payload);
    },
  });

  startAppListening({
    actionCreator: logoutThunk.pending,
    effect: () => {
      stopAllReplicationTracking();
    },
  });
}
